import { Col, Form, FormInstance, Input, Row, Select } from 'antd';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { FloatingFormItem, InputDate, InputSearch } from '@/components/_common';
import { useStates } from '@/core/domain/zipcode/States/States.hooks';
import {
  InvoiceDocReferencedFormValues,
  InvoiceDocReferencedIssuerType,
  InvoiceDocReferencedPayload,
} from '@/core/domain/invoice/referencedDocument/referencedDocument.types';
import { useDefaultEmptyFieldError } from '@/hooks';

interface Props {
  form?: FormInstance<InvoiceDocReferencedFormValues>;
  openSearch?: () => void;
  isEdit?: boolean;
  setIssuerType: (value: InvoiceDocReferencedIssuerType) => void;
  issuerType: InvoiceDocReferencedIssuerType;
}

const searchTitle = {
  Cliente: 'layout.menu.fiscal.invoice.referenced.select.customer',
  Fornecedor: 'layout.menu.fiscal.invoice.referenced.select.provider',
  Seguradora: 'layout.menu.fiscal.invoice.referenced.select.insuranceCompany',
};

function InvoiceDocReferencedIssuerForm({
  form,
  openSearch,
  issuerType,
  setIssuerType,
}: Props) {
  const intl = useIntl().formatMessage;
  const { data: states, isFetching } = useStates();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const handleValuesChange = (changedValues: InvoiceDocReferencedPayload) => {
    if (changedValues.issuerType) {
      setIssuerType(changedValues.issuerType);
    }
  };

  const ufCode = Form.useWatch('ufCode', form);

  useEffect(() => {
    if (states?.data[0] && ufCode) {
      const state = states?.data.filter((state) => state.codeIbge === ufCode);

      form?.setFieldsValue({
        uf: `${state[0].code}`,
        ufIbge: state[0].codeIbge,
      });
    }
  }, [ufCode]);

  return (
    <Form
      layout="vertical"
      form={form}
      onValuesChange={handleValuesChange}
      preserve={false}
    >
      <Row gutter={16}>
        <Col lg={8} span={24}>
          <FloatingFormItem
            label={intl({ id: 'invoice.create.referenceDocument.table.type' })}
            name="issuerType"
            initialValue="Cliente"
          >
            <Select
              onChange={() =>
                form?.setFieldsValue({ issuer: undefined, issuerId: undefined })
              }
            >
              <Select.Option value="Cliente">
                {intl({
                  id: 'layout.menu.fiscal.invoice.referenced.select.customer',
                })}
              </Select.Option>
              <Select.Option value="Fornecedor">
                {intl({
                  id: 'layout.menu.fiscal.invoice.referenced.select.provider',
                })}
              </Select.Option>
              <Select.Option value="Seguradora">
                {intl({
                  id: 'layout.menu.fiscal.invoice.referenced.select.insuranceCompany',
                })}
              </Select.Option>
            </Select>
          </FloatingFormItem>
        </Col>

        <Form.Item hidden name="issuerId" />

        <Col lg={16} span={24}>
          <FloatingFormItem
            label={intl({ id: searchTitle[issuerType] })}
            name="issuer"
          >
            <InputSearch buttonProps={{ onClick: openSearch }} />
          </FloatingFormItem>
        </Col>

        <Col lg={4} span={24}>
          <FloatingFormItem
            label={intl({
              id: 'invoice.create.referenceDocument.table.series',
            })}
            name="serie"
            rules={[defaultEmptyFieldError]}
          >
            <Input maxLength={5} />
          </FloatingFormItem>
        </Col>

        <Col lg={4} span={24}>
          <FloatingFormItem
            label={intl({ id: 'invoice.create.referenceDocument.table.model' })}
            name="model"
            rules={[defaultEmptyFieldError]}
          >
            <Input maxLength={2} />
          </FloatingFormItem>
        </Col>

        <Col lg={4} span={24}>
          <FloatingFormItem
            label={intl({
              id: 'invoice.create.referenceDocument.table.number',
            })}
            name="number"
            rules={[defaultEmptyFieldError]}
          >
            <Input maxLength={9} />
          </FloatingFormItem>
        </Col>

        <Col lg={8} span={24}>
          <FloatingFormItem
            label={intl({
              id: 'invoice.create.referenceDocument.table.issueDate',
            })}
            name="emissionDate"
            rules={[defaultEmptyFieldError]}
          >
            <InputDate />
          </FloatingFormItem>
        </Col>

        <Form.Item hidden name="uf" />

        <Form.Item hidden name="ufIbge" />

        <Col lg={4} span={24}>
          <FloatingFormItem
            label={intl({ id: 'invoice.create.referenceDocument.table.uf' })}
            name="ufCode"
            rules={[defaultEmptyFieldError]}
          >
            <Select
              loading={isFetching}
              disabled={isFetching}
              options={states?.data?.map((state) => ({
                value: state.codeIbge,
                label: state.code,
              }))}
            />
          </FloatingFormItem>
        </Col>
      </Row>
    </Form>
  );
}

export default InvoiceDocReferencedIssuerForm;
